import PropTypes from "prop-types";
import { BsX } from "react-icons/bs";
import { RemoveScroll } from "react-remove-scroll";
import { Portal } from "react-portal";
import { animated, to, useTransition } from "@react-spring/web";
import useMobileDetect from "../Utils/hooks/useMobileDetect";
import useEscapeButton from "../Utils/hooks/useEscapeButton";
import classNames from "classnames";

/**
 * FloatingModal renders a portal-based modal with optional header/footer and
 * scrollable content support while keeping legacy behavior intact.
 *
 * @param {Object} props - Component props.
 * @param {React.ReactNode} props.children - Modal body content.
 * @param {boolean} [props.isFluid=false] - Use fluid container for full width.
 * @param {() => void} props.toggle - Toggle the modal open/close.
 * @param {boolean} props.state - Modal open state.
 * @param {React.ReactNode} [props.header] - Optional header content.
 * @param {React.ReactNode} [props.footer] - Optional footer content.
 * @param {"sm" | "md" | "lg" | "xl" | "fullscreen"} [props.size="md"] - Modal width size.
 * @param {boolean} [props.isScrollable=false] - Scroll only content area.
 * @returns {JSX.Element} Floating modal component.
 */

const FloatingModal = ({
  children,
  isFluid = false,
  toggle,
  state,
  header = null,
  footer = null,
  size = "md",
  isScrollable = false,
  onScroll,
  zIndex = 1050,
  closeOnBackgroundClick = true,
  mobileBottomOffset = "4rem",
}) => {
  const isMobile = useMobileDetect();

  // Map size to class modifier for consistent sizing.
  const sizeClass = size ? `floating-modal--${size}` : "";
  // Enable content-only scrolling when requested.
  const scrollableClass = isScrollable ? "floating-modal--scrollable" : "";
  const transitions = useTransition(state ? [true] : [], {
    from: { opacity: 0, y: isMobile ? 16 : 32, scale: isMobile ? 1 : 0.98 },
    enter: { opacity: 1, y: 0, scale: 1 },
    leave: { opacity: 0, y: isMobile ? 16 : 24, scale: isMobile ? 1 : 0.98 },
    config: { tension: 500, friction: 30 },
  });

  useEscapeButton(() => state && toggle());

  // useLayoutEffect(() => {
  //   if (state) {
  //     document.body.style.overflow = "hidden";
  //   } else {
  //     document.body.style.overflow = "auto";
  //   }
  //   return () => {
  //     document.body.style.overflow = "auto";
  //   };
  // }, [state]);

  return <Portal container={document.body}>{transitions((style) => (

    <RemoveScroll enabled={isMobile ? false : state ? true : false}>
      <animated.div
        style={{ zIndex: zIndex, "--mobile-bottom-offset": mobileBottomOffset }}
        className={classNames("floating-modal", sizeClass, scrollableClass)}
      >
        <animated.div
          className="bg"
          style={{ opacity: style.opacity }}
          onClick={closeOnBackgroundClick ? toggle : undefined}
        />
        <animated.div
          style={{
            opacity: style.opacity,
            transform: to(
              [style.y, style.scale],
              (y, scale) => `translateY(${y}px) scale(${scale})`,
            ),
          }}
          className={classNames(
            { "container-fluid": isFluid, container: !isFluid },
            "modal-body",
            { "modal-body--scrollable": isScrollable && !isMobile },
          )}
        >
          <button
            type="button"
            className="close-button"
            onClick={toggle}
            aria-label="Close"
          >
            <BsX className="close-icon" />
          </button>
          {/* Header is optional and stays visible when scrollable */}
          {header ? (
            <div className="modal-header d-flex align-items-center justify-content-between">
              {header}
            </div>
          ) : null}
          {/* Content is optionally scrollable to keep header/footer in place */}
          {isScrollable ? (
            <div className="modal-content-scroll" onScroll={onScroll}>
              {children}
            </div>
          ) : (
            children
          )}
          {/* Footer is optional and stays visible when scrollable */}
          {footer ? <div className="modal-footer">{footer}</div> : null}
        </animated.div>
      </animated.div>
    </RemoveScroll>

  ))}</Portal>;
};

FloatingModal.propTypes = {
  children: PropTypes.node.isRequired,
  isFluid: PropTypes.bool,
  toggle: PropTypes.func.isRequired,
  state: PropTypes.bool.isRequired,
  header: PropTypes.node,
  footer: PropTypes.node,
  size: PropTypes.oneOf(["sm", "md", "lg", "xl", "fullscreen"]),
  isScrollable: PropTypes.bool,
  onScroll: PropTypes.func,
};

export const FloatingModalHeader = ({ title, subtitle }) => {
  return (
    <div className="d-flex flex-column gap-2">
      <h5 className="title">{title}</h5>
      {subtitle && <p className="subtitle">{subtitle}</p>}
    </div>
  );
};

export default FloatingModal;
